import { Component, Inject, computed, effect, signal } from '@angular/core'
import { MapGeoJSONFeature } from 'maplibre-gl'
import { MapEventsService } from 'src/app/core/services/map-events.service'
import { Layers } from 'src/app/shared/layers-config/layers'
import { DetailsModel } from 'src/app/shared/models/details.model'
import { format } from 'date-fns'
import { WarningDataModel } from 'src/app/shared/models/warning-event.model'
import { AppPaths } from 'src/app/app-routing.module'
import { AppStateService } from 'src/app/core/services/app-state.service'
import { firstValueFrom } from 'rxjs/internal/firstValueFrom'
import { AreaSimWarningDataModel } from 'src/app/shared/models/sim-warning-creation.model'
import { Feature, LineString } from 'geojson'
import { getTimeZoneLabel } from 'src/app/shared/utils/date.utils'
import { AppConfigModel } from 'src/app/core/models/app-config.model'
import { APP_CONFIG } from 'src/app/app.config'
import { getEmailLink } from '../../../utils/simulation-warning-details-utils'
import { DataSimulationWarningService } from 'src/app/shared/services/data-simulation-warning.service'
import { LayerSimulationWarningService } from 'src/app/shared/layers/layer-simulation-warning.service'
import { MapService } from 'src/app/core/services/map.service'
import { ToastService } from 'src/app/shared/services/toast.service'
import { HttpErrorResponse } from '@angular/common/http'

@Component({
  selector: 'app-simulation-area-warnings-details',
  templateUrl: './simulation-area-warnings-details.component.html',
  standalone: false,
})
export class SimulationAreaWarningsDetailsComponent {
  public selectedFeatureId = ''
  private readonly _details = signal<Map<string, DetailsModel>>(new Map<string, DetailsModel>())
  public readonly details = this._details.asReadonly()
  public hasDetails = computed(() => this.details().size > 0)
  public rawFeature: string | undefined
  public features: MapGeoJSONFeature[] = []
  public zoomLevel: number | undefined
  public index: number = 0
  public isTestWarning: boolean = false
  public showRaw: boolean = true
  public bearing = ''
  public isLoading: boolean = false
  public refreshIntervalId: NodeJS.Timeout | undefined
  public mailToLink = ''

  constructor(
    @Inject(APP_CONFIG) private readonly config: AppConfigModel,
    private readonly mapService: MapService,
    private readonly mapEventService: MapEventsService,
    public appState: AppStateService,
    private dataService: DataSimulationWarningService,
    private simulationTestLayer: LayerSimulationWarningService,
    private toastService: ToastService,
  ) {
    effect(() => {
      const features = this.mapEventService.clickedFeatures()
      this.isTestWarning = false
      this.showRaw = true
      this._details.set(new Map<string, DetailsModel>())
      this.features.length != features.length ? (this.index = 0) : this.index
      if (features[this.index]?.layer?.id === `${Layers.SimWarning.name}-hexagons`) {
        this.mapService.removeMarker()
        this.features = features
        this.setCurrentFeature(this.index)
      }
    })
  }

  toggleFeature(index: number) {
    this.index = index
    this.setCurrentFeature(index)
  }

  setCurrentFeature(index: number = this.index) {
    this.index = index
    if (
      this.selectedFeatureId &&
      this.selectedFeatureId == this.features[this.index].id?.toString()
    ) {
      this.selectedFeatureId = ''
      this.close()
      return
    }
    this.mailToLink = getEmailLink(this.features[this.index])
    this.selectedFeatureId = this.features[this.index].id?.toString() ?? ''
    const properties: WarningDataModel = this.features[this.index].properties as WarningDataModel
    this.rawFeature = JSON.stringify(this.features[this.index], undefined, 2)
    this.mapWarningsToDetails(properties)
    this.appState.setState(AppPaths.Simulation, {
      selectedFeatureId: this.selectedFeatureId,
    })
  }

  mapWarningsToDetails(properties: WarningDataModel) {
    let rawMetric = {
      title: 'Hazards',
      items: [
        {
          description: 'Global ID',
          value: this.selectedFeatureId,
        },
        {
          description: 'Warning UUID',
          value: properties.warningUUID,
        },
        {
          description: 'H3 ID',
          value: properties.h3id,
        },

        {
          description: 'Country Code',
          value: properties.countryCode,
        },
        {
          description: 'Type',
          value: properties.name,
        },
        {
          description: `Valid time (${getTimeZoneLabel(new Date(properties['startTime']))})`,
          value: `${
            properties['startTime']
              ? format(new Date(properties['startTime']), 'yyyy-MM-dd HH:mm')
              : '-'
          } - ${
            properties['expiryTime']
              ? format(new Date(properties['expiryTime']), 'yyyy-MM-dd HH:mm')
              : '-'
          }`,
        },
        ...(properties.confidence
          ? [
              {
                description: 'Confidence',
                value: properties.confidence.toString(),
              },
            ]
          : []),
        ...(properties.dfrs
          ? [
              {
                description: 'Dfrs',
                value: properties.dfrs,
              },
            ]
          : []),
      ],
    }
    properties.target
      ? rawMetric.items.push({ description: 'Target', value: properties.target })
      : null

    this.setDetail('RawMetric', rawMetric)
  }

  private setDetail(key: string, value: DetailsModel): void {
    this._details.update((current) => {
      const next = new Map(current)
      next.set(key, value)
      return next
    })
  }

  close() {
    this.appState.setState(AppPaths.Simulation, {
      selectedFeatureId: undefined,
    })
    this.selectedFeatureId = ''
    this._details.set(new Map<string, DetailsModel>())
  }

  async deleteWarning() {
    let feature = this.features[this.index] as unknown as Feature<LineString, WarningDataModel>

    let newWarning: AreaSimWarningDataModel = {
      warningName: feature.properties.name,
      longitude: JSON.parse(feature.properties.eventCoordinates)[0],
      latitude: JSON.parse(feature.properties.eventCoordinates)[1],
      isCancelled: true,
      traceId: feature.properties.traceId,
      ttl: feature.properties.expiryTime - feature.properties.startTime,
      targets: feature.properties.target ? [feature.properties.target] : [],
      h3id: feature.properties.h3id,
      countryCode: feature.properties.countryCode,
      dfrsClassification:
        feature.properties.dfrs === 'only'
          ? 'DFRS_ONLY'
          : feature.properties.dfrs === 'partly'
            ? 'DFRS_PARTLY'
            : 'DFRS_NONE',
    }

    this.isLoading = true
    try {
      const result: any = await firstValueFrom(this.dataService.createAreaTestWarnings(newWarning))
      this.toastService.show({ type: 'success', message: 'Warning deletion initialized' })
      this.close()
      clearInterval(this.refreshIntervalId)
      this.refreshIntervalId = setInterval(() => this.simulationTestLayer.reload(), 2000)

      setTimeout(
        (function (id_local) {
          return function () {
            clearInterval(id_local)
          }
        })(this.refreshIntervalId),
        30000,
      )
    } catch (error) {
      const status = error instanceof HttpErrorResponse ? ` (${error.status})` : ''
      this.toastService.show({ type: 'error', message: `Error deleting warning${status}` })
    } finally {
      this.isLoading = false
    }
  }
}
